import * as THREE from 'three';
import { CAMERA_FRAMING_MINIMUM_DEPTH } from '../../_data/cameraFraming.js';
import { resolveLayoutAnchorWorldPosition } from '../../rendering/_pure/entityAnchorResolution.js';
import { resolveCameraFramingPose, validateCameraFraming } from './cameraFraming.js';
export function resolveCameraAnchor(frame, anchor) {
    const target = resolveLayoutAnchorWorldPosition({
        anchor,
        context: {
            entityConstellations: frame.constellations.filter((entry) => entry.constellationKind === 'entity'),
            entityItemsByCollectionKey: new Map(frame.itemCollections.flatMap((collection) => collection.itemCollectionKind === 'entity' ? [[collection.itemCollectionKey, collection.items]] : [])),
            itemParametricStateByKey: frame.itemParametricState?.entityItemByKey ?? {},
            entityConstellationParametricStateByKey: frame.constellationParametricState?.entityConstellationByKey ?? {},
        },
    });
    if (target === null || target.length !== 3 || !target.every(Number.isFinite)) {
        throw new Error(`Camera target could not be resolved: ${JSON.stringify(anchor)}`);
    }
    return [...target];
}
export function validateCameraStation(station) {
    if (station.fov !== undefined && (!Number.isFinite(station.fov) || station.fov <= 0 || station.fov >= 180)) {
        throw new Error('Camera station FOV must be between zero and 180 degrees.');
    }
    if (station.stationKind === 'explicit' || station.stationKind === 'anchored') {
        const vector = station.stationKind === 'explicit' ? station.position : station.offset;
        if (vector.length !== 3 || !vector.every(Number.isFinite)) {
            throw new Error('Camera station requires three finite position/offset coordinates.');
        }
    }
    else if (station.stationKind === 'framing') {
        validateCameraFraming({ ...station.framing, ...(station.fov === undefined ? {} : { fov: station.fov }) });
        for (const requirement of station.requirements ?? [])
            validateCameraEnvelope(requirement.bounds);
    }
    else {
        throw new Error('Unsupported camera station kind.');
    }
}
function validateCameraEnvelope(bounds) {
    validateCameraFraming('points' in bounds
        ? { framingKind: 'extent', bounds, direction: [0, 0, 1], padding: 1 }
        : { bounds, direction: [0, 0, 1], padding: 1 });
}
export function cameraEnvelopeFits(position, target, fov, aspect, bounds, padding = 1, minimumDepth = CAMERA_FRAMING_MINIMUM_DEPTH) {
    const eye = new THREE.Vector3(...position);
    const aim = new THREE.Vector3(...target);
    if (eye.distanceToSquared(aim) < 1e-20)
        return false;
    const basis = new THREE.Matrix4().lookAt(eye, aim, new THREE.Vector3(0, 1, 0));
    const right = new THREE.Vector3().setFromMatrixColumn(basis, 0);
    const up = new THREE.Vector3().setFromMatrixColumn(basis, 1);
    const back = new THREE.Vector3().setFromMatrixColumn(basis, 2);
    const vertical = Math.tan(fov * Math.PI / 360);
    const horizontal = vertical * aspect;
    const fitsPoint = (point, radius) => {
        const relative = new THREE.Vector3(point[0], point[1], point[2]).sub(eye);
        const depth = -relative.dot(back);
        return depth - radius >= minimumDepth - 1e-9
            && Math.abs(relative.dot(right)) + radius * Math.hypot(1, horizontal) <= depth * horizontal + 1e-9
            && Math.abs(relative.dot(up)) + radius * Math.hypot(1, vertical) <= depth * vertical + 1e-9;
    };
    if ('radius' in bounds)
        return fitsPoint(bounds.center, bounds.radius * padding);
    const center = new THREE.Vector3(...bounds.center);
    return bounds.points.every((point) => fitsPoint(new THREE.Vector3(...point).sub(center).multiplyScalar(padding).add(center).toArray(), 0));
}
export function resolveCameraViewPose(frame, view, start, aspect, state) {
    const graph = frame.spaceRenderOptions?.camera?.graph;
    const station = graph?.stations[view.station];
    if (!station)
        throw new Error(`Camera station could not be resolved: ${view.station}`);
    validateCameraStation(station);
    if (!Number.isFinite(aspect) || aspect <= 0)
        throw new Error('Camera station requires a positive viewport aspect ratio.');
    const target = resolveCameraAnchor(frame, view.target);
    const targets = Object.values(graph.views).filter((entry) => entry.station === view.station)
        .map((entry) => resolveCameraAnchor(frame, entry.target));
    const requirements = station.stationKind === 'framing'
        ? (station.requirements ?? []).map((entry) => ({ target: resolveCameraAnchor(frame, entry.target), bounds: entry.bounds }))
        : [];
    const positioningAnchor = station.stationKind === 'anchored' ? resolveCameraAnchor(frame, station.anchor)
        : station.stationKind === 'framing' && station.positioningAnchor
            ? resolveCameraAnchor(frame, station.positioningAnchor) : undefined;
    const signature = JSON.stringify({ station, targets, requirements, positioningAnchor });
    const cached = state?.current;
    const sameStation = cached?.stationId === view.station;
    const fov = station.fov ?? (sameStation ? cached.fov : start.fov);
    let position;
    if (sameStation && cached.signature === signature && cached.aspect === aspect && cached.fov === fov) {
        position = [...cached.position];
    }
    else if (station.stationKind === 'explicit') {
        position = [...station.position];
    }
    else if (station.stationKind === 'anchored') {
        position = [
            station.offset[0] + positioningAnchor[0],
            station.offset[1] + positioningAnchor[1],
            station.offset[2] + positioningAnchor[2],
        ];
    }
    else {
        const fitted = resolveCameraFramingPose({ ...station.framing, fov }, start, aspect);
        const origin = new THREE.Vector3(...positioningAnchor ?? station.framing.bounds.center);
        const [x, y, z] = station.framing.direction;
        const scale = Math.max(Math.abs(x), Math.abs(y), Math.abs(z));
        const direction = new THREE.Vector3(x / scale, y / scale, z / scale).normalize();
        const initialDistance = new THREE.Vector3(...station.framing.bounds.center).distanceTo(new THREE.Vector3(...fitted.position));
        const at = (distance) => origin.clone().addScaledVector(direction, distance).toArray();
        position = positioningAnchor ? at(initialDistance) : fitted.position;
        const pairs = requirements.length > 0 ? requirements
            : targets.map((entry) => ({ target: entry, bounds: station.framing.bounds }));
        const minimumDepth = station.framing.framingKind === 'extent'
            ? station.framing.minimumDepth ?? CAMERA_FRAMING_MINIMUM_DEPTH : CAMERA_FRAMING_MINIMUM_DEPTH;
        const fits = (candidate) => pairs.every((entry) => cameraEnvelopeFits(candidate, entry.target, fov, aspect, entry.bounds, station.framing.padding, minimumDepth));
        const fixedAim = positioningAnchor && pairs.every((entry) => entry.target.every((value, axis) => value === positioningAnchor[axis]))
            && targets.every((entry) => entry.every((value, axis) => value === positioningAnchor[axis]));
        if (fixedAim) {
            const matrix = new THREE.Matrix4().lookAt(direction, new THREE.Vector3(), new THREE.Vector3(0, 1, 0));
            const right = new THREE.Vector3().setFromMatrixColumn(matrix, 0);
            const up = new THREE.Vector3().setFromMatrixColumn(matrix, 1);
            const back = new THREE.Vector3().setFromMatrixColumn(matrix, 2);
            const vertical = Math.tan(fov * Math.PI / 360);
            const horizontal = vertical * aspect;
            const forward = direction.dot(back);
            const rightRate = forward + direction.dot(right) / horizontal;
            const leftRate = forward - direction.dot(right) / horizontal;
            const topRate = forward + direction.dot(up) / vertical;
            const bottomRate = forward - direction.dot(up) / vertical;
            if (Math.min(rightRate, leftRate, topRate, bottomRate, forward) <= 0) {
                throw new Error(`Camera station ${view.station} cannot fit along its positioning ray.`);
            }
            let distance = 1e-8;
            for (const { bounds } of pairs) {
                const center = new THREE.Vector3(...bounds.center);
                const points = 'points' in bounds ? bounds.points : [bounds.center];
                const radius = 'radius' in bounds ? bounds.radius * station.framing.padding : 0;
                for (const point of points) {
                    const relative = new THREE.Vector3(...point).sub(center)
                        .multiplyScalar(station.framing.padding).add(center).sub(origin);
                    const depth = relative.dot(back);
                    const horizontalExtent = radius * Math.hypot(1, horizontal);
                    const verticalExtent = radius * Math.hypot(1, vertical);
                    distance = Math.max(distance, (depth + (relative.dot(right) + horizontalExtent) / horizontal) / rightRate, (depth + (-relative.dot(right) + horizontalExtent) / horizontal) / leftRate, (depth + (relative.dot(up) + verticalExtent) / vertical) / topRate, (depth + (-relative.dot(up) + verticalExtent) / vertical) / bottomRate, (depth + radius + minimumDepth) / forward);
                }
            }
            position = at(distance);
            if (!position.every(Number.isFinite) || !fits(position)) {
                throw new Error(`Camera station ${view.station} could not satisfy its fixed-target framing requirements.`);
            }
        }
        else if (!fits(position)) {
            let low = initialDistance;
            let high = Math.max(low, minimumDepth);
            let found = false;
            for (let iteration = 0; iteration < 64; iteration++) {
                high *= 2;
                if (fits(at(high))) {
                    found = true;
                    break;
                }
            }
            if (!found)
                throw new Error(`Camera station ${view.station} has no feasible shared framing position.`);
            for (let iteration = 0; iteration < 64; iteration++) {
                const middle = low + (high - low) / 2;
                if (fits(at(middle)))
                    high = middle;
                else
                    low = middle;
            }
            position = at(high);
            if (!position.every(Number.isFinite) || !fits(position)) {
                throw new Error(`Camera station ${view.station} could not satisfy its framing requirements.`);
            }
        }
    }
    if (state)
        state.current = { stationId: view.station, signature, aspect, position: [...position], fov };
    return { position, target, fov };
}
